import type { ObstacleMask } from "../fluid/types";
import {
  fontDeclaration,
  layoutGlyphs,
  type GlyphPlacement,
  type NormalizedPoint,
} from "./layout";

export interface GlyphObstacleMask extends ObstacleMask {
  /** Glyph placement id for each cell, or -1 when the cell is fluid. */
  glyphIds: Int16Array;
}

export class TextMaskGenerator {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement("canvas");
    const context = this.canvas.getContext("2d", {
      alpha: true,
      willReadFrequently: true,
    });
    if (!context) {
      throw new Error("Canvas 2D is required to generate the text obstacle.");
    }
    this.context = context;
  }

  generate(
    word: string,
    placements: readonly GlyphPlacement[],
    width: number,
    height: number,
  ): GlyphObstacleMask {
    this.canvas.width = width;
    this.canvas.height = height;
    const binary = new Uint8Array(width * height);
    const glyphIds = new Int16Array(width * height);
    glyphIds.fill(-1);

    const layouts = layoutGlyphs(
      this.context,
      word,
      placements,
      width,
      height,
    );
    for (const layout of layouts) {
      if (layout.placement.character === " ") {
        continue;
      }
      this.context.clearRect(0, 0, width, height);
      this.context.font = fontDeclaration(layout.fontSize);
      this.context.textAlign = "center";
      this.context.textBaseline = "alphabetic";
      this.context.fillStyle = "#fff";
      this.context.save();
      this.context.translate(layout.centerX, layout.centerY);
      this.context.rotate(layout.placement.rotation);
      this.context.fillText(
        layout.placement.character,
        0,
        layout.baseline - layout.centerY,
      );
      this.context.restore();

      const rgba = this.context.getImageData(0, 0, width, height).data;
      for (let index = 0; index < binary.length; index += 1) {
        const alpha = rgba[index * 4 + 3] ?? 0;
        if (alpha >= 112) {
          binary[index] = 255;
          glyphIds[index] = layout.placement.id;
        }
      }
    }

    return { width, height, data: binary, glyphIds };
  }
}

export function hitTestMask(
  mask: ObstacleMask,
  normalizedX: number,
  normalizedY: number,
): boolean {
  return sampleMaskIndex(mask, normalizedX, normalizedY) !== null;
}

export function hitTestGlyphMask(
  mask: GlyphObstacleMask,
  normalizedX: number,
  normalizedY: number,
): number | null {
  const index = sampleMaskIndex(mask, normalizedX, normalizedY);
  if (index === null) {
    return null;
  }
  const glyphId = mask.glyphIds[index] ?? -1;
  return glyphId >= 0 ? glyphId : null;
}

function sampleMaskIndex(
  mask: ObstacleMask,
  normalizedX: number,
  normalizedY: number,
): number | null {
  if (
    normalizedX < 0 ||
    normalizedX > 1 ||
    normalizedY < 0 ||
    normalizedY > 1
  ) {
    return null;
  }
  const x = Math.min(
    mask.width - 1,
    Math.floor(normalizedX * mask.width),
  );
  const y = Math.min(
    mask.height - 1,
    Math.floor(normalizedY * mask.height),
  );
  const index = y * mask.width + x;
  return (mask.data[index] ?? 0) > 0 ? index : null;
}

export function moveGlyph(
  placements: GlyphPlacement[],
  glyphId: number,
  center: NormalizedPoint,
): boolean {
  const glyph = placements.find((placement) => placement.id === glyphId);
  if (!glyph) {
    return false;
  }
  glyph.center = center;
  return true;
}
